import CollapsibleArea from "@/components/common/CollapsibleArea";
import { useCanvas } from "@/hooks/useCanvas";
import { useVotingStatus } from "@/hooks/useVotingStatus";
import { useCanvasLayout } from "@/layouts/CanvasLayoutContext";
import { Box, Title, SimpleGrid, Text, Flex, useMantineTheme, Stack } from "@mantine/core";
import { StyledMarkdown } from "@/ui/components/StyledMarkdown";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import OverviewCard from "./CanvasOverview/OverviewCard";
import { shouldUseLegacyCanvasPresentation } from "./canvasPresentation";
import { buildCanvasOverviewSections } from "./CanvasOverview/canvasOverviewSections";
import { QuestionContext } from "./CanvasOverview/QuestionContext";

type Props = {
  showHeader?: boolean;
};

type ComputedLayoutProps = {
  width: string;
  columns: number;
  headerSize: "h1" | "h2";
  topMargin: string;
  headerMargin: string;
  cardSectionMargin: string;
};

function computeLayoutProps(numItems: number, maxPageWidth: string): ComputedLayoutProps {
  if (numItems < 4) {
    return {
      width: `calc(${maxPageWidth} * 0.67)`,
      columns: 1,
      headerSize: "h1",
      topMargin: "xl",
      headerMargin: "xs",
      cardSectionMargin: "calc(var(--mantine-spacing-xl) * 1.0)",
    };
  }
  return {
    width: "100%",
    columns: 2,
    headerSize: "h2",
    topMargin: "xs",
    headerMargin: "xs",
    cardSectionMargin: "calc(var(--mantine-spacing-xl) * 1.25)",
  };
}

// Re-implementing as a clean, fresh page using Mantine primitives
function CanvasOverviewPage({ showHeader }: Props) {
  const theme = useMantineTheme();
  const { sizes } = theme.other;
  const {
    canvasResult: { data: canvasData },
    isParticipation,
  } = useCanvas();
  const { canvasId: externalId } = useParams<{ canvasId: string }>();
  const { t } = useTranslation("pages");
  const { setDarkBackground } = useCanvasLayout();
  const { votingEnabled, isVotingClosed } = useVotingStatus();

  console.log("votingEnabled", votingEnabled);
  console.log("isVotingClosed", isVotingClosed);

  useEffect(() => {
    setDarkBackground(true);
  }, [setDarkBackground]);

  const themes = canvasData?.themes ?? [];
  const overviewSections = buildCanvasOverviewSections(themes, canvasData?.question_groups ?? []);
  const hasQuestionGrouping = overviewSections.some((section) => section.questionId !== null);
  const layoutProps = computeLayoutProps(themes.length, sizes.maxPageWidthDesktop);
  const focusAreaCount = themes.length;
  const respondentCount = canvasData?.stats?.respondent_count;
  const useLegacyPresentation = shouldUseLegacyCanvasPresentation(canvasData);
  const headerText = canvasData?.text?.summary ?? "";
  const intro = canvasData?.text?.body ?? "";
  const hasLegacyHeaderText = headerText.trim().length > 0;
  const hasLegacyIntro = intro.trim().length > 0;
  const shouldRenderLegacyHeaderSection = useLegacyPresentation && (hasLegacyHeaderText || hasLegacyIntro);
  const summaryText =
    respondentCount != null
      ? t("canvas.overview.header.summary", {
          count: focusAreaCount,
          contributionsText: t("canvas.overview.header.contributions", {
            count: respondentCount,
          }),
        })
      : t("canvas.overview.header.responsesGrouped", {
          count: focusAreaCount,
        });

  const renderThemeCard = (
    group: (typeof themes)[number],
    groupIndex: number,
    questionContext?: { label: string; number: number },
  ) => {
    const title = group.text.title ?? t("canvas.overview.groupFallback", { index: groupIndex + 1 });
    const description = group.text.summary ?? group.text.body ?? "";

    return (
      <Box key={groupIndex} h="100%">
        <OverviewCard
          title={title}
          description={description}
          groupIndex={groupIndex}
          icon={undefined}
          externalId={externalId ?? ""}
          isParticipation={isParticipation}
          group={group}
          votingEnabled={votingEnabled}
          isVotingClosed={isVotingClosed}
          duration={200}
          questionContext={questionContext}
        />
      </Box>
    );
  };

  // Guard against missing data
  if (!canvasData) {
    return null;
  }

  return (
    <Box
      h="100%"
      style={{
        display: "flex",
        flexDirection: "column",
      }}
      mih="30rem"
      mx="auto"
      maw={sizes.maxPageWidthDesktop}
      p="md"
      pb="xs"
    >
      {showHeader && (
        <Title
          order={2}
          size={layoutProps.headerSize}
          mb="md"
          ta="center"
          mt={layoutProps.topMargin}
          c="var(--text-on-dark)"
        >
          {canvasData?.meta?.title || canvasData?.text?.title || t("canvas.overview.title")}
        </Title>
      )}
      <Flex justify="center" mt={layoutProps.headerMargin}>
        {shouldRenderLegacyHeaderSection ? (
          <Text ta="center" fs="italic" fz="lg" lh="1.33" maw={layoutProps.width} c="var(--text-on-dark-muted)">
            {headerText}
          </Text>
        ) : (
          <Stack gap={6} align="center" maw={layoutProps.width} w="100%">
            <Text fw={600} size="sm" lh={1.35} ta="center" maw="42rem" c="var(--text-on-dark-muted)">
              {summaryText}
            </Text>
          </Stack>
        )}
      </Flex>
      {shouldRenderLegacyHeaderSection && intro && (
        <CollapsibleArea collapseHeight="4rem">
          <Flex justify="center" mt="xs">
            <Box w="100%" maw={layoutProps.width} style={{ textAlign: "center" }}>
              <StyledMarkdown content={intro} />
            </Box>
          </Flex>
        </CollapsibleArea>
      )}

      {themes.length > 0 && hasQuestionGrouping ? (
        <Stack align="center" mt={layoutProps.cardSectionMargin} gap="xl" w="100%">
          {overviewSections.map((section, sectionIndex) => (
            <Box
              key={section.questionId ?? `ungrouped-${sectionIndex}`}
              w="100%"
              maw={layoutProps.width}
              pt={sectionIndex > 0 ? "xs" : 0}
            >
              {section.questionLabel && section.questionNumber ? (
                <Box mb="md" px="md">
                  <QuestionContext label={section.questionLabel} number={section.questionNumber} inverted />
                </Box>
              ) : null}
              <SimpleGrid cols={{ base: 1, md: layoutProps.columns }} spacing="md">
                {section.themes.map(({ group, groupIndex }) =>
                  renderThemeCard(
                    group,
                    groupIndex,
                    section.questionLabel && section.questionNumber
                      ? { label: section.questionLabel, number: section.questionNumber }
                      : undefined,
                  ),
                )}
              </SimpleGrid>
            </Box>
          ))}
        </Stack>
      ) : themes.length > 0 ? (
        <Flex justify="center" align="center" mt={layoutProps.cardSectionMargin}>
          <SimpleGrid cols={{ base: 1, md: layoutProps.columns }} spacing="md" maw={layoutProps.width}>
            {themes.map((group, groupIndex) => renderThemeCard(group, groupIndex))}
          </SimpleGrid>
        </Flex>
      ) : (
        <Text mt="md" c="var(--text-on-dark-muted)">
          {t("canvas.overview.noFocusGroups")}
        </Text>
      )}
    </Box>
  );
}

export default CanvasOverviewPage;
